import clsx from "clsx";
import { GridLoader } from "react-spinners";
import { Blog } from "../../../../core/models/db";
import { useIdentityStore, useSnackbarStore } from "../../../../core/store";
import { UploadFile } from "../../../components/drag-drop/model/UploadFile";
import { FileService } from "../../../components/drag-drop/services/file.service";
import { CContainer } from "@coreui/react-pro";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LoaderContext } from "../../../components/loader/context/LoaderContext";
import { useTranslation } from "react-i18next";
import { HiArrowNarrowLeft } from "react-icons/hi";
import { BASE_ROUTE, ROUTE_NAME_ID_2, RouteName } from "../../../../core/constants/route_name";
import { useBlog, useSEO } from "../../../../core/hooks/db/fisioteduca/useFisioteduca";
import { Util } from "../../../../core/util/util";
import * as MessageSnackbarDefault from "../../../../core/constants/message-snackbar/msgFormDefault";

import BlogForm from "./BlogForm";
import moment from "moment";


const BlogDetail = () => {
  const { t } = useTranslation();
  const { identity } = useIdentityStore((state) => state);
  const navigate = useNavigate();

  const [blog, setBlog] = useState<Blog>(undefined);
  const [loading, setLoading] = useState(false);
  const [formHeight, setFormHeight] = useState(0);
  const formRef = useRef(null);
  const [typeAction, setTypeAction] = useState<"add" | "update">(null);

  const { addSnackbar, setMaxSnackbars } = useSnackbarStore();


  const { getBlogById, addBlog: add, updateBlog: update } = useBlog();
  const { checkSpecificParam: checkSpecificParamSEO } = useSEO(); // Si tuvieras un check param para SEO

  const { id } = useParams();

  // Ajusta si necesitas
  useEffect(() => {
    if (formRef.current) {
      setFormHeight(formRef.current.clientHeight);
    }
  }, [loading, blog]);

  /**
   * Cargar Blog
   */
  useEffect(() => {
    (async () => {
      setLoading(true);
      if (id) {
        const result = await getBlogById(id);
        if (result && result.ok && result.item) {
          setBlog(result.item);
          setTypeAction("update");
        } else {
          console.warn("No se encontró ningún id de Blog");
          const pagesRoutes = t("routes", { returnObjects: true });
          const newRoute = ROUTE_NAME_ID_2({}, pagesRoutes, RouteName.blog);
          navigate(`${BASE_ROUTE}${newRoute}`);
        }
      } else {
        // Crear
        let newBlog = new Blog();
        setBlog(newBlog);
        setTypeAction("add");
      }
      setLoading(false);
    })();
  }, []);

  /**
   * Update
   */
  const updateBlog = async (
    blog: Blog,
    imageItem: {
      oldImage: string;
      image: string;
      nameImage: string;
    }
  ) => {
    try {
      setLoading(true);
      const blogCopy = { ...blog };
      const { oldImage, image, nameImage } = imageItem;

      // Manejo de imagen
      if (image && image.startsWith("blob")) {
        const lastDotIndex = nameImage.lastIndexOf(".");
        // const name = lastDotIndex > -1 ? nameImage.slice(0, lastDotIndex) : nameImage;
        const name = Date.now() + "_" + Math.random().toString(36).substring(7);
        const ext = lastDotIndex > -1 ? nameImage.slice(lastDotIndex) : ".png";
        const formattedName = name.replace(/ /g, "_");
        let file = await Util.transformBlobToFile(
          image,
          formattedName + "_" + Date.now() + ext
        );
        blogCopy.image = await createFile(file, blogCopy.id);
      }

      if (oldImage && oldImage !== blogCopy.image) {
        await FileService.delete(oldImage);
      }

      if (!blogCopy.startPublicationDate) {
        blogCopy.startPublicationDate = moment().subtract(5, "minutes").toDate();
      }

      const result = await update(blogCopy);
      if (result.ok) {
        setBlog(result.item);
      }

      addSnackbar(MessageSnackbarDefault.successEditDefault);
    } catch (error) {
      console.error("Error al actualizar Blog", error);
      addSnackbar(MessageSnackbarDefault.errorDefault);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Add
  //  */
  // const addNewBlog = async (
  //   blog: Blog,
  //   imageItem: {
  //     oldImage: string;
  //     image: string;
  //     nameImage: string;
  //   }
  // ) => {
  //   try {
  //     setLoading(true);
  //     const { oldImage, image, nameImage } = imageItem;
  //     if (image && image.startsWith("blob")) {
  //       let file = await Util.transformBlobToFile(
  //         image,
  //         blog.title + "_" + new Date().getTime() + ".png"
  //       );
  //       blog.image = await createFile(file, blog.id);
  //     }

  //     if (!blog.startPublicationDate) {
  //       blog.startPublicationDate = moment().add(5, "minutes").toDate();
  //     }

  //     const result = await add(blog);
  //     if (result.ok) {
  //       setTypeAction("update");
  //       setBlog(result.item);
  //     }
  //   } catch (error) {
  //     console.error("Error al crear Blog", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  /**
  * Add
  */
  const addNewBlog = async (
    blog: Blog,
    imageItem: {
      oldImage: string;
      image: string;
      nameImage: string;
    }
  ) => {
    try {
      setLoading(true);

      // 1. Crear una copia del blog sin la imagen
      const blogToCreate = { ...blog };
      delete blogToCreate.image;

      // 2. Crear el Blog sin la imagen
      const createResult = await add(blogToCreate);
      if (!createResult.ok) {
        throw new Error("Error al crear el Blog");
      }

      const newBlog = createResult.item;
      setTypeAction("update");
      setBlog(newBlog);

      const newBlogId = newBlog.id;

      // 3. Subir la imagen principal si existe y es un blob
      let mainImagePath = newBlog.image;
      if (imageItem.image && imageItem.image.startsWith("blob:")) {
        const lastDotIndex = imageItem.nameImage.lastIndexOf(".");
        // const name = lastDotIndex > -1 ? imageItem.nameImage.slice(0, lastDotIndex) : imageItem.nameImage;
        const name = Date.now() + "_" + Math.random().toString(36).substring(7);
        const ext = lastDotIndex > -1 ? imageItem.nameImage.slice(lastDotIndex) : ".png";
        const formattedName = name.replace(/ /g, "_");
        let file = await Util.transformBlobToFile(
          imageItem.image,
          formattedName + "_" + Date.now() + ext
        );
        mainImagePath = await createFile(file, newBlogId);
      }

      // 4. Actualizar el Blog con la ruta de la imagen
      const updatedBlog = {
        ...newBlog,
        image: mainImagePath,
        startPublicationDate: newBlog.startPublicationDate || moment().subtract(5, "minutes").toDate(),
      };

      const updateResult = await update(updatedBlog);
      if (updateResult.ok) {
        setBlog(updateResult.item);
        addSnackbar(MessageSnackbarDefault.successAddDefault);
      } else {
        throw new Error("Error al actualizar el Blog con la imagen");
      }
    } catch (error) {
      console.error("Error al crear Blog", error);
      addSnackbar(MessageSnackbarDefault.errorDefault);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Subir archivo
   */
  const createFile = async (file: any, id: string) => {
    try {
      const { data } = await FileService.add(new UploadFile(file, ["images", "blog", id]), null);
      if (data.ok) {
        const pathRelative = `/${data.item[0].destination}/${data.item[0].filename}`;
        return pathRelative;
      } else {
        return undefined;
      }
    } catch (e) {
      throw e;
    }
  };

  /**
   * Checar SEO param (si lo usas)
   */
  const checkBlogSlug = async (id: string, value: string) => {
    try {
      const result = await checkSpecificParamSEO(id, value, "slug");
      return result;
    } catch (error) {
      console.error("Error al verificar slug de Blog", error);
      return false;
    }
  };

  /**
   * Manejo final
   */
  const handleRecord = (
    value: Blog,
    imageItem: {
      oldImage: string;
      image: string;
      nameImage: string;
    }
  ) => {
    if (typeAction === "add" && !value?.id) {
      addNewBlog(value, imageItem);
    } else if (typeAction === "update" && value?.id) {
      updateBlog(value, imageItem);
    } else {
      console.warn("No hay acción definida para Blog");
    }
  };

  return (
    <CContainer style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-full justify-between items-center flex pt-4 pb-5">
        <div className="flex flex-col">
          <div
            className="flex items-center hover:underline cursor-pointer mb-5"
            onClick={() => {
              const pagesRoutes = t("routes", { returnObjects: true });
              let newRoute = ROUTE_NAME_ID_2({}, pagesRoutes, RouteName.blog);
              navigate(`${BASE_ROUTE}${newRoute}`);
            }}
          >
            <HiArrowNarrowLeft size={20} className="mr-1" />
            {t("screens.blog.labelGoBack") || "Volver a Blogs"}
          </div>

          <span className="text-4xl" style={{ fontWeight: 500 }}>
            {t("screens.blog.title") || "Blogs"}
          </span>
          <span className="small text-medium-emphasis">
            {typeAction === "add"
              ? t("screens.blog.labelCreate") || "Crear Blog"
              : t("screens.blog.labelDetail") || "Detalle Blog"}
          </span>
        </div>
      </div>

      <div className="w-full flex justify-center mb-10">
        <div className="w-full flex justify-center gap-4 flex-col xl:flex-row ">
          <div
            className={clsx(`flex flex-col w-full justify-center items-center`, {
              "xl:w-[100%]": blog?.id,
            })}
          >
            <div className="bg-white block p-4 md:p-7 shadow-md md:rounded-lg w-full md:w-[70%]">
              <div className="text-2xl font-medium mb-4">
                {t("screens.blog.subtitle_1") || "Información del Blog"}
              </div>
              <div className="w-full flex flex-col md:flex-row justify-center md:gap-4">
                {loading && (
                  <div
                    className="w-full flex flex-col justify-center items-center"
                    style={{ height: 547 }}
                  >
                    <GridLoader color="#3d4f65" size={25} speedMultiplier={0.7} />
                  </div>
                )}

                {!loading && blog && (
                  <div ref={formRef} className="p-0">
                    <BlogForm
                      initialBlog={blog}
                      checkBlogSlug={checkBlogSlug}
                      onSubmit={(item, imageItem) => {
                        handleRecord(item, imageItem);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </CContainer>
  );
};

export default BlogDetail;
